import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import {
  getLenis,
  gsap,
  prefersReducedMotion,
  refreshScrollTrigger,
  ScrollTrigger,
} from "../../lib/motion";
import PixelRunner, { type PixelRunnerState } from "./PixelRunner";

const SECTION_MAP: Record<string, string[]> = {
  "/": ["#process", "#book-consult", "#about"],
  "/projects": ["#projects"],
  "/packages": ["#packages"],
};

const KEYBOARD_STEP = 0.08;

type Marker = {
  id: string;
  el: HTMLElement;
};

function resolveSections(pathname: string): Marker[] {
  const selectors = SECTION_MAP[pathname] ?? [];
  const markers: Marker[] = [];
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el instanceof HTMLElement) {
      markers.push({ id: sel, el });
    }
  }
  return markers;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Fixed side track with a pixel runner that follows scroll and can scrub the page.
 */
const ScrollGuide: React.FC = () => {
  const location = useLocation();
  const trackRef = useRef<HTMLDivElement>(null);
  const runnerWrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<ScrollTrigger | null>(null);
  const jumpTimeoutRef = useRef<number | null>(null);
  const lastIndexRef = useRef(0);
  const lastDirRef = useRef<"down" | "up">("down");
  const stateRef = useRef<PixelRunnerState>("idle");
  const isDraggingRef = useRef(false);
  const progressRef = useRef(0);
  const lastPointerYRef = useRef(0);

  const [markers, setMarkers] = useState<Marker[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [runnerState, setRunnerState] = useState<PixelRunnerState>("idle");
  const [facing, setFacing] = useState<"down" | "up">("down");
  const [visible, setVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);
  const reduced = prefersReducedMotion();

  const clearJumpTimer = () => {
    if (jumpTimeoutRef.current != null) {
      window.clearTimeout(jumpTimeoutRef.current);
      jumpTimeoutRef.current = null;
    }
  };

  const setStateSafe = useCallback((next: PixelRunnerState) => {
    if (stateRef.current === next) return;
    if (stateRef.current === "jump" && next === "run") return;
    stateRef.current = next;
    setRunnerState(next);
  }, []);

  const triggerJump = useCallback(() => {
    if (prefersReducedMotion()) return;
    clearJumpTimer();
    stateRef.current = "jump";
    setRunnerState("jump");
    jumpTimeoutRef.current = window.setTimeout(() => {
      stateRef.current = "idle";
      setRunnerState("idle");
      jumpTimeoutRef.current = null;
    }, 420);
  }, []);

  const updateSectionFromProgress = useCallback(
    (progress: number, multi: boolean, fireJump: boolean) => {
      if (!multi) {
        setSliderValue(Math.round(progress * 100));
        return;
      }
      const idx = Math.min(
        markers.length - 1,
        Math.max(0, Math.round(progress * (markers.length - 1)))
      );
      if (idx !== lastIndexRef.current) {
        lastIndexRef.current = idx;
        setActiveIndex(idx);
        if (fireJump) triggerJump();
      }
      setSliderValue(Math.round(progress * 100));
    },
    [markers.length, triggerJump]
  );

  const applyProgress = useCallback(
    (progress: number, opts?: { scroll?: boolean; fireJump?: boolean }) => {
      const track = trackRef.current;
      const runnerWrap = runnerWrapRef.current;
      const st = triggerRef.current;
      if (!track || !runnerWrap || !st) return;

      const p = clamp(progress, 0, 1);
      progressRef.current = p;

      const trackH = track.clientHeight;
      const runnerH = runnerWrap.offsetHeight;
      const travel = Math.max(0, trackH - runnerH);
      gsap.set(runnerWrap, { xPercent: -50, y: p * travel });

      if (opts?.scroll !== false) {
        const scrollY = st.start + p * (st.end - st.start);
        const lenis = getLenis();
        if (lenis && !prefersReducedMotion()) {
          lenis.scrollTo(scrollY, { immediate: true });
        } else {
          window.scrollTo(0, scrollY);
        }
      }

      updateSectionFromProgress(p, markers.length > 1, opts?.fireJump ?? false);
    },
    [markers.length, updateSectionFromProgress]
  );

  // Discover sections after route / layout settle
  useEffect(() => {
    let cancelled = false;

    const discover = () => {
      if (cancelled) return;
      const found = resolveSections(location.pathname);
      setMarkers(found);
      setVisible(found.length >= 1);
      lastIndexRef.current = 0;
      setActiveIndex(0);
      progressRef.current = 0;
      setSliderValue(0);
      stateRef.current = "idle";
      setRunnerState("idle");
      refreshScrollTrigger();
    };

    const t1 = window.setTimeout(discover, 80);
    const t2 = window.setTimeout(discover, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      clearJumpTimer();
    };
  }, [location.pathname]);

  // ScrollTrigger: map page scroll to runner Y on the track
  useEffect(() => {
    const track = trackRef.current;
    const runnerWrap = runnerWrapRef.current;
    if (!track || !runnerWrap || markers.length === 0) return;

    const first = markers[0].el;
    const last = markers[markers.length - 1].el;
    const multi = markers.length > 1;

    const ctx = gsap.context(() => {
      gsap.set(runnerWrap, { xPercent: -50, y: 0 });

      const st = ScrollTrigger.create({
        trigger: first,
        start: multi ? "top center" : "top 80%",
        endTrigger: last,
        end: multi ? "bottom center" : "bottom 20%",
        scrub: reduced ? false : 0.4,
        onUpdate: (self) => {
          if (isDraggingRef.current) return;

          const progress = self.progress;
          progressRef.current = progress;
          const trackH = track.clientHeight;
          const runnerH = runnerWrap.offsetHeight;
          const travel = Math.max(0, trackH - runnerH);
          gsap.set(runnerWrap, { xPercent: -50, y: progress * travel });

          const dir: "down" | "up" = self.direction === 1 ? "down" : "up";
          if (dir !== lastDirRef.current) {
            lastDirRef.current = dir;
            setFacing(dir);
          }

          updateSectionFromProgress(progress, multi, true);

          if (!reduced) {
            const moving = Math.abs(self.getVelocity()) > 40;
            setStateSafe(moving ? "run" : "idle");
          }
        },
        onLeave: () => {
          if (!isDraggingRef.current) setStateSafe("idle");
        },
        onLeaveBack: () => {
          if (!isDraggingRef.current) setStateSafe("idle");
        },
      });

      triggerRef.current = st;
    }, track);

    refreshScrollTrigger();

    return () => {
      triggerRef.current = null;
      ctx.revert();
      clearJumpTimer();
    };
  }, [markers, reduced, setStateSafe, updateSectionFromProgress]);

  const progressFromClientY = useCallback((clientY: number): number => {
    const track = trackRef.current;
    const runnerWrap = runnerWrapRef.current;
    if (!track || !runnerWrap) return progressRef.current;

    const rect = track.getBoundingClientRect();
    const runnerH = runnerWrap.offsetHeight;
    const travel = Math.max(1, rect.height - runnerH);
    // Center runner on pointer within the travel range
    const y = clientY - rect.top - runnerH / 2;
    return clamp(y / travel, 0, 1);
  }, []);

  const endDrag = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setDragging(false);
    setStateSafe("idle");
  }, [setStateSafe]);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!triggerRef.current) return;
    e.preventDefault();
    isDraggingRef.current = true;
    setDragging(true);
    lastPointerYRef.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
    clearJumpTimer();
    stateRef.current = "run";
    setRunnerState("run");

    const p = progressFromClientY(e.clientY);
    applyProgress(p, { scroll: true, fireJump: true });
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!isDraggingRef.current) return;
    const dy = e.clientY - lastPointerYRef.current;
    if (dy !== 0) {
      const dir: "down" | "up" = dy > 0 ? "down" : "up";
      if (dir !== lastDirRef.current) {
        lastDirRef.current = dir;
        setFacing(dir);
      }
      lastPointerYRef.current = e.clientY;
    }
    if (!reduced) {
      stateRef.current = "run";
      setRunnerState("run");
    }
    const p = progressFromClientY(e.clientY);
    applyProgress(p, { scroll: true, fireJump: true });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    endDrag();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    let delta = 0;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") delta = KEYBOARD_STEP;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") delta = -KEYBOARD_STEP;
    else if (e.key === "Home") {
      e.preventDefault();
      applyProgress(0, { scroll: true, fireJump: true });
      return;
    } else if (e.key === "End") {
      e.preventDefault();
      applyProgress(1, { scroll: true, fireJump: true });
      return;
    } else {
      return;
    }
    e.preventDefault();
    const dir: "down" | "up" = delta > 0 ? "down" : "up";
    lastDirRef.current = dir;
    setFacing(dir);
    applyProgress(progressRef.current + delta, {
      scroll: true,
      fireJump: true,
    });
  };

  // End drag if pointer is lost globally
  useEffect(() => {
    const onBlur = () => endDrag();
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [endDrag]);

  if (!visible) return null;

  return (
    <div className="scroll-guide">
      <div className="scroll-guide__rail" ref={trackRef}>
        <div className="scroll-guide__line" />
        {markers.map((m, i) => {
          const topPct =
            markers.length === 1 ? 50 : (i / (markers.length - 1)) * 100;
          return (
            <span
              key={m.id}
              className={`scroll-guide__marker${
                i === activeIndex ? " scroll-guide__marker--active" : ""
              }`}
              style={{ top: `${topPct}%` }}
              aria-hidden
            />
          );
        })}
        <div
          className={`scroll-guide__runner-wrap${
            dragging ? " scroll-guide__runner-wrap--dragging" : ""
          }`}
          ref={runnerWrapRef}
        >
          <button
            type="button"
            className="scroll-guide__control"
            role="slider"
            aria-orientation="vertical"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={sliderValue}
            aria-label="Scroll through page sections"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onKeyDown}
          >
            <PixelRunner
              state={runnerState}
              facing={facing}
              reducedMotion={reduced}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScrollGuide;
